import Link from "next/link"
import { ArrowRight, type LucideIcon } from "lucide-react"
import type { ReactNode } from "react"

/*
 * The numbered before/during/after journey, shared by the clinic-facing home
 * page and the owner-facing /owners page so both tell the same story in the
 * same visual language — only the copy and audience change.
 *
 * Each phase carries its own accent colour so the steps read as distinct
 * stages of one timeline (lighter blues deepen through the journey, gold for
 * the "after" keepsake phase).
 */

export type JourneyItem = {
  icon: LucideIcon
  title: string
  body: string
  visual: ReactNode
  sources?: { label: string; href: string }[]
  link?: { label: string; href: string }
}

export type JourneyPhase = {
  phase: string
  accent: string
  items: JourneyItem[]
}

interface JourneyTimelineProps {
  phases: JourneyPhase[]
  /** Section background — the phase markers ring against it. */
  railBackground?: string
}

export function JourneyTimeline({
  phases,
  railBackground = "#FAF9F6",
}: JourneyTimelineProps) {
  return (
    <div className="relative space-y-14 md:space-y-12">
      {/* Timeline rail connecting the phase markers */}
      <div
        aria-hidden
        className="absolute left-[15px] top-4 bottom-16 w-[2px] rounded-full md:hidden"
        style={{
          background: `linear-gradient(to bottom, ${phases
            .map((p) => p.accent)
            .join(", ")})`,
          opacity: 0.25,
        }}
      />
      {phases.map(({ phase, accent, items }, i) => (
        <div key={phase} className="relative">
          <h3 className="flex items-center gap-3 mb-6">
            <span
              className="relative inline-flex items-center justify-center w-8 h-8 rounded-full text-white text-[14px] font-bold flex-shrink-0"
              style={{
                backgroundColor: accent,
                // Punches the marker out of the timeline rail behind it.
                boxShadow: `0 0 0 4px ${railBackground}`,
              }}
            >
              {i + 1}
            </span>
            <span
              className="inline-flex items-center rounded-full px-4 py-1.5 text-[16px] md:text-[15px] font-semibold text-white"
              style={{ backgroundColor: accent }}
            >
              {phase}
            </span>
          </h3>
          <div
            className={`pl-11 md:pl-0 grid gap-6 md:gap-5 sm:grid-cols-1 ${
              items.length % 2 === 1 ? "grid-cols-3 lg:grid-cols-2" : "grid-cols-2"
            }`}
          >
            {items.map(({ icon: Icon, title, body, visual, sources, link }) => (
              <div
                key={title}
                className="bg-white rounded-xl shadow-sm border border-[#002e65]/[0.06] border-t-[3px] overflow-hidden flex flex-col"
                style={{ borderTopColor: accent }}
              >
                {/* What the family/clinic actually gets — mini mockup */}
                <div className="relative h-[132px] flex-shrink-0 flex items-center justify-center overflow-hidden bg-gradient-to-br from-[#eef5fb] to-[#dceaf6] border-b border-[#002e65]/[0.05]">
                  {visual}
                </div>
                <div className="p-6 md:p-5 flex flex-col flex-1">
                  <div className="flex items-center gap-2.5 mb-2.5">
                    <div
                      className="w-8 h-8 rounded-full text-white flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: accent }}
                    >
                      <Icon className="w-4 h-4" />
                    </div>
                    <h4 className="text-[18px] font-semibold text-[#002e65] leading-snug">
                      {title}
                    </h4>
                  </div>
                  <p className="text-[#3a5a7c] leading-relaxed text-[15px]">{body}</p>
                  {sources && (
                    <p className="mt-3 text-[12px] text-[#94a3b8] leading-relaxed">
                      Sources:{" "}
                      {sources.map((source, j) => (
                        <span key={source.href}>
                          {j > 0 && " · "}
                          <a
                            href={source.href}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-[#3a5a7c]"
                          >
                            {source.label}
                          </a>
                        </span>
                      ))}
                    </p>
                  )}
                  {link && (
                    <Link
                      href={link.href}
                      className="mt-3 inline-flex items-center gap-1 text-[14px] font-semibold text-[#004da9] hover:text-[#002e65] transition-colors"
                    >
                      {link.label}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  )
}
